<!-- eslint-disable no-console -->
<template>
    <div ref="pageContainer"
        :class="['page page--light wallet-page', { 'page--embed': partnerAppEmbedCustomization }]"
    >
        <header
            :class="['row__header', { 'row__header--partner-app': isPartnerApp }]"
        >
            <back-button :isAbsolute="false"
                :isBlack="true"
                :isHiddenOnDesktop="true"
            />

            <h1 class="row__header--title">{{ $t('wallet.wallet') }}</h1>
            <redeem-voucher v-if="isPartnerApp"
                :isPartnerApp="isPartnerApp"
                :isPartnerAppWallet="true"
            />
        </header>
        <section ref="walletContainer"
            class="wallet"
        >
            <div v-if="partnerAppEmbedCustomization"
                class="wallet__back-button--embed"
            >
                <back-button :isAbsolute="true" />
            </div>
            <loading-spinner v-if="isLoading" />
            <div v-else
                class="container"
            >
                <div class="wallet__top">
                    <div class="wallet__logo">
                        <currency-symbol v-if="isFiatDefaultCurrency"
                            :symbol="preferredFiatCurrency.symbol"
                            :fontSize="[4.3, 13.4]"
                            color="#ffffff"
                        />
                        <img v-else
                            src="@webapp/assets/images/wallet/chz-logo.svg"
                        >
                    </div>
                    <div class="wallet__details">
                        <h1>{{ $t('wallet.availableBalance') }}</h1>
                        <span class="wallet__main-balance">
                            {{ centralizedWallet.balance }}
                        </span>
                        <div :class="['wallet__other-balance', {'wallet__other-balance--add-padding': isFiatDefaultCurrency}]">
                            <span v-if="!isFiatDefaultCurrency"
                                class="wallet__fiat"
                            >≈{{ centralizedWallet.altCurrencyBalance }}</span>
                            <div class="wallet__locked">
                                {{ $t('wallet.locked') }}
                                <img src="@webapp/assets/images/wallet/lock.svg"
                                    alt="icon lock"
                                >
                                <span class="wallet__locked--amount">{{ centralizedWallet.lockedBalance }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="walletButtons.length"
                v-scroll="animateBar"
                class="wallet__buttons"
            >
                <div ref="buttonContainer"
                    class="wallet__buttons-container"
                >
                    <div ref="walletButtonBackground"
                        class="wallet__buttons-background"
                    />
                    <button v-for="button in walletButtons"
                        :key="button.name"
                        type="button"
                        :class="['wallet__button', {'wallet__button--disabled': button.isDisabled}] "
                        :disabled="button.isDisabled"
                        @click="onClickBtn(button)"
                    >
                        <img :src="`../../../assets/images/wallet/${button.icon}.svg`"
                            :alt="`button icon ${button.icon}`"
                        >
                        <div class="wallet__button-text-container">
                            <div class="wallet__button-text">{{ $t(button.text) }}</div>
                            <div v-if="button.isDisabled"
                                class="wallet__coming-soon"
                            >
                                {{ $t('global.comingSoon').toUpperCase() }}
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </section>
        <section class="content"
            :style="{'--dist': shaddowDistanceToBorder}"
        >
            <div v-if="!shouldHideTabsFeature"
                ref="walletToggle"
                class="container wallet-toggle"
            >
                <toggle-options id="wallet-toggle"
                    :options="toggleOptions"
                    :partnerAppData="partnerAppData"
                    :userId="userId"
                    @toggleOption="onToggleOption"
                />
            </div>

            <keep-alive>
                <router-view />
            </keep-alive>
        </section>
    </div>
</template>

<script>
// 3rd party
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Helpers
import {
    getWithdrawalGatewayURL,
} from '@shared/utils/withdraw';
import {
    isKycEligible,
} from '@webapp/helpers/kyc';
import {
    ExternalWindow,
    WindowEvents,
} from '@webapp/services/window';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import redeemVoucher from '@webapp/components/redeem-voucher/redeem-voucher.vue';
import toggleOptions from '@webapp/components/status-toggle/status-toggle.vue';
import currencySymbol from '@webapp/components/currency-symbol.vue';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';
import sharedStoreTypes from '@shared/store/types';
import authStoreTypes from '@shared/store/modules/auth/types';
import {
    EventNames,
} from '@shared/utils/analytics-keys';

// Local Types
import localEpicTypes from '@webapp/store/types.js';

import {
    RegulationType,
} from '@shared/store/modules/wallet/types';

// Socios variables
import socios from '@webapp/socios.json';

import {
    showUserLockedModal,
} from '@webapp/router/index';

// Jurisdiction requirements
import {
    checkLegalRequirementsFor,
} from '@shared/services/jurisdiction/requirements';
import {
    PERMISSION_CODE,
} from '@shared/config/user-permissions';

export default {
    name: 'wallet-v2',
    components: {
        backButton,
        redeemVoucher,
        toggleOptions,
        currencySymbol,
    },
    data() {
        return {
            toggleOptions: [
                {
                    id: 'fan-tokens',
                    path: '/wallet/fan-tokens',
                    name: this.$t('wallet.fanTokens'),
                },
                {
                    id: 'digital-collectibles',
                    path: '/wallet/digital-collectibles',
                    name: this.$t('wallet.digitalCollectibles'),
                },
            ],
            shaddowDistanceToBorder: 222,
        };
    },
    computed: {
        ...mapGetters([
            'defaultCurrency',
            'preferredFiatCurrency',
            'currentRegulation',
            'isUsaRegulation',
            'authenticationData',
            'isPartnerApp',
            'userId',
            'device',
            'partnerAppData',
            'userDetails',
            'partnerAppEmbedCustomization',
            'hasRestrictedPayments',
        ]),

        ...mapGetters('auth', ['getSessionAuthToken']),

        ...mapGetters('wallet', [
            'centralizedWallet',
            'isWithdrawalEnabled',
            'isTopupEnabled',
            'regulatoryRestrictionLimits',
        ]),

        isFiatDefaultCurrency() {
            return this.defaultCurrency.type === 'fiat';
        },

        shouldHideTabsFeature() {
            return socios.digitalCollectibles.restricted.includes(this.currentRegulation);
        },

        // If currencies and balances are not yet fetched, set to isLoading to avoid showing incorrect values and symbols
        isLoading() {
            return (!this.defaultCurrency.id) || this.centralizedWallet.balance === '0';
        },

        walletButtons() {
            return [
                ...(this.isPartnerApp && !this.isWithdrawalEnabled ? [] : [
                    {
                        name: 'send',
                        text: 'wallet.send',
                        class: 'send',
                        icon: 'withdraw',
                        url: '/wallet/withdraw',
                        isDisabled: !this.isWithdrawalEnabled,
                    },
                ]),
                ...(this.isUsaRegulation || this.isPartnerApp ? [] : [
                    {
                        name: 'receive',
                        text: 'wallet.receive',
                        class: 'receive',
                        icon: 'receive',
                        url: '/wallet/receive',
                    },
                ]),
                {
                    name: 'market',
                    text: 'wallet.market',
                    class: 'marketplate',
                    icon: 'market',
                    url: '/trading',
                },
                ...(!this.isTopupEnabled && this.isPartnerApp ? [] : [
                    {
                        name: 'topup',
                        text: 'wallet.topup',
                        class: 'topup',
                        icon: 'top-up',
                        url: '/wallet/topup',
                        isDisabled: !this.isTopupEnabled,
                    },
                ]),

            ];
        },
        isUserElegibleToTransfer() {
            if (this.userDetails.status === 'locked') {
                showUserLockedModal();

                return false;
            }

            const isEligible = isKycEligible({
                minLevel: 3,
                showModal: true,
                desiredComponent: 'kyc-level-up-modal-withdrawal',
            });

            if (!isEligible) {
                return false;
            }

            return true;
        },
    },
    async created() {
        await this.getWallet({
            chilizCurrencyId: socios.chilizCurrencyId,
        });
    },
    mounted() {
        this.$webTrackingInstance?.sendEvent(
            EventNames.WalletMainPage,
            [
                {
                    key: 'device_id',
                    value: this.device.uuid,
                },
                {
                    key: 'userId',
                    value: this.userId,
                },
            ]
        );
    },
    methods: {
        ...mapActions({
            getWallet: sharedEpicTypes.GET_WALLET,
            getUserPermissions: sharedEpicTypes.GET_USER_PERMISSIONS,
            addRouterErrors: sharedStoreTypes.ADD_ROUTER_ERRORS,
            setModal: localEpicTypes.SET_MODAL,
            resetModal: localEpicTypes.RESET_MODAL,
        }),

        ...mapActions('auth', {
            fetchSessionAuthToken: authStoreTypes.GET_SESSION_AUTH_TOKEN,
        }),

        ...mapActions('wallet', ['fetchWalletRemainingLedger']),

        onToggleOption(options) {
            const { path } = options.find(({ isSelected }) => isSelected);

            // In case we click on back button, we don't need to re-route to the same path
            if (this.$route.path.includes(path)) {
                return;
            }

            if (path.includes('digital-collectibles')) {
                this.$webTrackingInstance?.sendEvent(
                    EventNames.PartnerDigitalCol,
                    [
                        {
                            key: 'partner',
                            value: this.partnerAppData.id,
                        },
                        {
                            key: 'user',
                            value: this.userId,
                        },
                        {
                            key: 'from',
                            value: 'wallet',
                        },
                    ]
                );
            }

            this.$router.push(path);
        },

        async openWithdrawalGateway() {
            await this.fetchWalletRemainingLedger({
                batchType: 'withdrawal',
                movementType: 'debit',
            });

            await this.fetchSessionAuthToken();

            const url = getWithdrawalGatewayURL({
                ...this.authenticationData,
                generatedCode: this.getSessionAuthToken,
                currency: this.centralizedWallet.currency,
                limits: this.regulatoryRestrictionLimits[RegulationType.Withdrawal] ?? {},
                webApp: true,
            });

            const gateway = new ExternalWindow({
                url,
                target: '_blank',
            });

            gateway.onMessage(($event) => {
                if ($event.data.type === WindowEvents.CLOSE_WINDOW) {
                    gateway.close();
                }
            });
        },

        onClickBtn({
            name,
            url,
        }) {
            if (name === 'send' && this.isUsaRegulation) {
                if (!this.isUserElegibleToTransfer) {
                    return;
                }

                try {
                    checkLegalRequirementsFor({
                        permissionCode: PERMISSION_CODE.WALLET_WITHDRAWAL,
                    }, {
                        onPermissionGranted: () => this.openWithdrawalGateway(),
                        onRequirementsFulfilled: () => {
                            this.getUserPermissions();
                            this.openWithdrawalGateway();
                        },
                    });
                } catch (error) {
                    this.addRouterErrors({
                        message: error.message,
                        route: 'openWithdrawalGateway',
                        permissionCode: PERMISSION_CODE.WALLET_WITHDRAWAL,
                    });
                }
            } else {
                if (name === 'topup') {
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.WalletTopupStart,
                        [
                            {
                                key: 'device_id',
                                value: this.device.uuid,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                        ]
                    );

                    if (this.hasRestrictedPayments) {
                        this.setModal({
                            isVisible: true,
                            isClosable: false,
                            componentToLoad: 'components/modals/us-payment-restrictions-info',
                            data: {
                                ctaOpts: {
                                    text: this.$t('global.button.goBack'),
                                    callback: () => {
                                        this.resetModal();
                                    },
                                },
                            },
                        });
                    }
                }

                this.$router.push(url);
            }
        },
        animateBar: function(el) {

            const walletCtn = this.$refs.walletContainer;
            const walletToggle = this.$refs.walletToggle;
            const parent = this.$refs.pageContainer;
            const container = this.$refs.buttonContainer;
            const buttonBackground = this.$refs.walletButtonBackground;
            const originalButtonsHeight = el.clientHeight;
            const originalTopPostition = getComputedStyle(el).getPropertyValue('top');
            const heightRatio = originalButtonsHeight / parseInt(originalTopPostition, 10);

            // Get distance to cover the blank around grey background, + 10 for overlay and hide border radius
            let distanceToBorder = ((parent.clientWidth - container.clientWidth) / 2);
            // Animation needs to be finisehed when we rich the bottom of the header;
            const totalHeight = walletCtn.clientHeight + document.getElementsByClassName('top-nav__header')['0'].clientHeight;
            const scrollDistance = window.innerWidth > 992
                ? totalHeight - (originalButtonsHeight / heightRatio)
                : totalHeight - (originalButtonsHeight / heightRatio) - 20;
            // 26px, difference from 2 states from FIGMA in height of the button bar.
            const headerShrink = 26;
            const margingBtwBarAndSelect = parseInt(getComputedStyle(walletCtn).getPropertyValue('marginBottom'), 10) || 6;

            // Be sure if we scroll over threshold, the animation is in deploeyd state/
            let isDeployed = false;


            // Reset distance to border on window resize to avoid overlays
            const setSizes = function() {
                distanceToBorder = ((parent.clientWidth - el.clientWidth) / 2);
            };

            window.addEventListener('resize', setSizes, true);

            const onScroll = function() {

                // Reset when scroll is too fast up ;

                const reset = function() {
                    el.style.top = originalTopPostition;
                    el.classList.remove('with-sticky-position');
                    walletCtn.style.paddingBottom = '0rem';
                    walletToggle.classList.remove('with-shaddow');
                };

                if (window.scrollY <= 0) {
                    reset();
                    buttonBackground.style.left = '0px';
                    buttonBackground.style.right = '0px';
                    buttonBackground.style.height = `${originalButtonsHeight}px`;
                    buttonBackground.style.borderRadius = '8px';
                    [...parent.querySelectorAll('.wallet__button')].forEach((button) => {
                        // Make Button Title desapear by opacity;
                        button.querySelector('.wallet__button-text-container').style.opacity = 1;
                    });
                    walletCtn.style.marginBottom = `${margingBtwBarAndSelect}rem`;

                    isDeployed = false;
                }

                if (window.scrollY > 0 && window.scrollY < scrollDistance) {

                    reset();
                    const percentDistance = window.scrollY / scrollDistance;

                    const targetWidth = `-${distanceToBorder * percentDistance}px`;
                    const targetHeight = `${originalButtonsHeight - (headerShrink * percentDistance)}px`;

                    buttonBackground.style.left = targetWidth;
                    buttonBackground.style.right = targetWidth;
                    buttonBackground.style.height = targetHeight;


                    [...parent.querySelectorAll('.wallet__button')].forEach((button) => {
                        // Make Button Title desapear by opacity;
                        button.querySelector('.wallet__button-text-container').style.opacity = (1 - (percentDistance * 5));
                    });

                    isDeployed = percentDistance >= 1;

                    // Keep the wallet container to the same distance of the bar, 6rem in CSS.
                    walletCtn.style.marginBottom = `${margingBtwBarAndSelect * (1 - percentDistance)}rem`;
                    buttonBackground.style.borderRadius = `${8 - (8 * percentDistance)}px`;
                }

                // Reset when scroll is too fast down, bee sure to be in the ending state;
                if (window.scrollY > scrollDistance) {

                    if (!isDeployed) {
                        buttonBackground.style.left = `-${distanceToBorder}px`;
                        buttonBackground.style.right = `-${distanceToBorder}px`;
                        buttonBackground.style.height = `-${originalButtonsHeight - headerShrink}px`;
                        buttonBackground.style.borderRadius = '0px';
                        walletToggle.classList.add('with-shaddow');
                        el.style.top = 0;
                        isDeployed = true;
                        walletCtn.style.paddingBottom = '10rem';
                    }

                    el.classList.add('with-sticky-position');

                }
                requestAnimationFrame(onScroll);
            };

            requestAnimationFrame(onScroll);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@webapp/modules/crypto-and-trading/styles/wallet/common.scss";

.row__header {
    background: $athens-gray-color;

    @include min-width(md) {
        display: none;
    }
}

.wallet-page {
    overflow: visible;
    z-index: 1;
}

.wallet {
    background: url("~@webapp/assets/images/wallet/wallet-bg.webp") no-repeat center;
    background-size: cover;
    padding-top: 5rem;
    color: $white-color;
    margin-bottom: 6rem;
    z-index: 10;
    box-sizing: border-box;

    &__back-button {
        &--embed {

            @include max-width(md) {
                display: none;
            }
        }
    }

    @include max-width(lg) {
        height: 32rem;
    }

    &__top {
        display: flex;

        @include max-width(lg) {
            flex-direction: column;
            align-items: center;
        }
    }

    &__symbol {
        width: 9rem;
        height: 9rem;
        margin: 0 2rem;
        flex-shrink: 0;
    }

    &__logo {
        margin: 0 5rem 0 3.5rem;

        img {
            @include max-width(lg) {
                width: 4.3rem;
                margin-bottom: 1.5rem;
            }
        }
    }

    &__details {
        display: flex;
        flex-direction: column;
        word-break: break-word;
    }

    h1 {
        font-size: 1.7rem;
        margin: 0 0 1.2rem 0;
        font-weight: normal;

        @include max-width(lg) {
            font-size: 1.1rem;
            text-align: center;
        }
    }

    &__main-balance {
        font-size: 4.8rem;
        font-weight: 700;
        margin-bottom: 1.5rem;

        @include max-width(lg) {
            font-size: 3.6rem;
        }
    }

    &__other-balance {
        display: flex;

        @include max-width(lg) {
            flex-direction: column;
            text-align: center;
        }

        &--add-padding {
            @include max-width(lg) {
                padding-top: 3rem;
                padding-bottom: 1rem;
            }
        }
    }

    &__fiat {
        font-size: 1.7rem;
        font-weight: 700;
        margin-right: 3rem;

        @include max-width(lg) {
            font-size: 1.5rem;
            margin: 0 0 2rem;
        }
    }

    &__locked {
        font-size: 1.5rem;

        @include max-width(lg) {
            font-size: 1.3rem;
        }

        img {
            margin: 0 0.6rem;
        }

        &--amount {
            font-weight: 700;
        }
    }

    &__buttons {
        position: relative;
        border-radius: 8px;
        top: 5rem;
        z-index: 1;
        box-sizing: border-box;

        @include max-width(xl) {
            padding-right: 3rem;
            padding-left: 3rem;
        }

        @include max-width(lg) {
            top: 2rem;
            padding-right: 1.7rem;
            padding-left: 1.7rem;
        }

        &:before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            right: 0;
            display: block;
            z-index: -1;
            background: rgba(255, 255, 255, 0.5);
            filter: blur(100px);
        }
    }

    &__buttons-container {
        max-width: 75.4rem;
        margin: 0 auto;
        width: calc(100% - 2rem);
        display: flex;
        align-items: center;
        justify-content: space-around;
        padding: 0 1rem;
        height: 10rem;
        position: relative;
    }

    &__buttons-background {
        background: url("~@webapp/assets/images/wallet/buttons-bg.webp") no-repeat center;
        background-size: cover;
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        right: 0;
        display: block;
        z-index: -1;
        border-radius: 8px;
    }

    &__button {
        border: 0;
        background: transparent;
        text-align: center;
        cursor: pointer;
        flex: 1;

        &--disabled {
            .wallet__button-text,
            img {
                opacity: 0.3;
            }
        }

        &-text {
            font-size: 1.3rem;
            font-weight: 700;
            color: $daintree-color;
            margin-top: 0.5rem;
        }
    }

    &__coming-soon {
        text-align: center;
        font-size: 1.2rem;
        font-weight: 700;
        color: $blue-ribbon-color;
        letter-spacing: 0.0678261px;
    }
}

.with-sticky-position {
    position: fixed;
    width: calc(100% - 25rem);

    @include max-width(md) {
        width: 100%;
    }
}

.content {
    background-color: $athens-gray-color;
    position: relative;
    padding-bottom: 15rem;
    display: flex;
    flex-direction: column;

    &:before {
        content: "";
        height: 27.5rem;
        width: 100%;
        background: linear-gradient(180deg, $white-color 0%, $athens-gray-color 70.48%);
        display: block;
        position: absolute;
        top: -6rem;
    }
}

.wallet-toggle {
    margin-top: 3.5rem;
    padding-bottom: 1rem;
    position: sticky;
    top: 100px;
    z-index: 3;

    ::v-deep .status-toggle {
        @include max-width(md) {
            width: unset;
        }

        &__option {
            background-color: $white-color;
            color: rgba($cyprus-color, 0.3);
            border-color: rgba($cyprus-color, 0.3);
            font-size: 1.5rem;
            padding: 0.8rem 3rem;

            &--active {
                background: $cyprus-color;
                border-color: $cyprus-color;
                color: $white-color;
                opacity: 1;
            }
        }
    }

    @include max-width(md) {
        margin-top: 4.5rem;
    }
}
</style>